import type { ReactNode } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from '../../../core/index.js'
import type { AcceptanceCriterion, PolicyDecision } from '../../types.js'

export const EDITOR_INPUT_CLASS = 'w-full border border-marine/30 rounded px-2 py-1 text-xs text-ink bg-white focus:outline-none focus:ring-2 focus:ring-coral'

const replaceAt = <T,>(items: readonly T[], index: number, item: T): T[] => items.map((each, at) => (at === index ? item : each))
const removeAt = <T,>(items: readonly T[], index: number): T[] => items.filter((_, at) => at !== index)

function RemoveButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} title={label} className="shrink-0 p-1 text-ink-mid hover:text-coral">
      <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
    </button>
  )
}

function ListShell({ label, hint, children, onAdd, addLabel }: { label: string; hint?: string; children: ReactNode; onAdd: () => void; addLabel: string }) {
  return (
    <div>
      <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-ink-mid">{label}</div>
      {hint && <p className="mb-1 text-[11px] text-ink-mid">{hint}</p>}
      <div className="flex flex-col gap-1.5">{children}</div>
      <Button icon={Plus} variant="secondary" size="sm" className="mt-1.5" onClick={onAdd}>
        {addLabel}
      </Button>
    </div>
  )
}

/** A list of short texts: the scope, what is out of it, the documents to keep in step. */
export function StringListEditor({ label, hint, items, onChange, addLabel, placeholder }: { label: string; hint?: string; items: readonly string[]; onChange: (items: string[]) => void; addLabel: string; placeholder?: string }) {
  return (
    <ListShell label={label} hint={hint} onAdd={() => onChange([...items, ''])} addLabel={addLabel}>
      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-1">
          <input value={item} placeholder={placeholder} onChange={(event) => onChange(replaceAt(items, index, event.target.value))} className={EDITOR_INPUT_CLASS} />
          <RemoveButton label={`Remove ${label.toLowerCase()} ${index + 1}`} onClick={() => onChange(removeAt(items, index))} />
        </div>
      ))}
    </ListShell>
  )
}

/** Gherkin-style scenarios, each naming the Cucumber section it adds to or updates when one is known. */
export function CriteriaEditor({ items, onChange }: { items: readonly AcceptanceCriterion[]; onChange: (items: AcceptanceCriterion[]) => void }) {
  return (
    <ListShell label="Acceptance criteria" hint="Each one needs a test. Name the Cucumber section it adds to or updates, if you know it." onAdd={() => onChange([...items, { scenario: '', cucumberSection: null }])} addLabel="Add a criterion">
      {items.map((item, index) => (
        <div key={index} className="flex items-start gap-1">
          <span className="w-5 shrink-0 pt-1 text-right text-[11px] font-bold text-ink-mid">{index + 1}.</span>
          <div className="flex flex-1 flex-col gap-1">
            <textarea rows={2} value={item.scenario} placeholder="Given … When … Then …" onChange={(event) => onChange(replaceAt(items, index, { ...item, scenario: event.target.value }))} className={EDITOR_INPUT_CLASS} />
            <input
              value={item.cucumberSection ?? ''}
              placeholder="Cucumber section (optional)"
              onChange={(event) => onChange(replaceAt(items, index, { ...item, cucumberSection: event.target.value === '' ? null : event.target.value }))}
              className={EDITOR_INPUT_CLASS}
            />
          </div>
          <RemoveButton label={`Remove criterion ${index + 1}`} onClick={() => onChange(removeAt(items, index))} />
        </div>
      ))}
    </ListShell>
  )
}

/** Every open policy decision the change touches, with the user's answer; an unanswered one keeps it from being submitted. */
export function DecisionsEditor({ items, onChange }: { items: readonly PolicyDecision[]; onChange: (items: PolicyDecision[]) => void }) {
  return (
    <ListShell label="Policy decisions" hint="Every open decision it touches, with the user's answer. It cannot be submitted while any is unanswered." onAdd={() => onChange([...items, { question: '', answer: null }])} addLabel="Add a decision">
      {items.map((item, index) => (
        <div key={index} className="flex items-start gap-1">
          <div className="flex flex-1 flex-col gap-1">
            <input value={item.question} placeholder="The question" onChange={(event) => onChange(replaceAt(items, index, { ...item, question: event.target.value }))} className={EDITOR_INPUT_CLASS} />
            <textarea
              rows={2}
              value={item.answer ?? ''}
              placeholder="The user's answer (open while empty)"
              onChange={(event) => onChange(replaceAt(items, index, { ...item, answer: event.target.value === '' ? null : event.target.value }))}
              className={EDITOR_INPUT_CLASS}
            />
          </div>
          <RemoveButton label={`Remove decision ${index + 1}`} onClick={() => onChange(removeAt(items, index))} />
        </div>
      ))}
    </ListShell>
  )
}
